import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  compose,
  curry,
  head,
  isEmpty,
  isNil,
  split,
} from 'ramda';
import { withStyles } from '@material-ui/core/styles';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { withApollo } from 'react-apollo';
import * as utils from '../../utils';
import { TeamContext } from '../TeamProvider';
import { UPSERT_APPLICATION } from './queries';

const styles = () => ({
  root: {
    margin: '0.5em',
    width: '20em',
    display: 'flex',
    flexFlow: 'row wrap',
    alignItems: 'center',
    justifyContent: 'space-around',
    marginBottom: '1em',
  },
  saveButton: {
    borderRadius: '0.125em',
    marginTop: '1em',
    marginRight: '0em',
    width: '5em',
  },
  helperText: {
    margin: '1em',
  },
});

const domainRegex = /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

class UnstyledCreateApplicationDialogue extends Component {
  baseState = {
    domain: '',
    error: null,
    isPstn: false,
  };

  handleChange = curry((isDevPortal, name, event) => {
    const inputText = event.target.value.toLowerCase();
    const val = isDevPortal ? this.extractAll(inputText) : this.extractAlphaAndSpecial(inputText);
    this.setState({ [name]: val });
    return val;
  });

  constructor(props) {
    super(props);

    this.state = this.baseState;
  }

  checkForCollisions = (domain) => {
    if (isNil(domain) || isEmpty(domain)) return;

    const { env } = this.props;

    if (!this.isDevPortal(env)) {
      this.setState({ error: !domainRegex.test(domain) ? new Error('Invalid Domain Name') : null });
    } else {
      this.setState({ error: null });
    }
  };

  isDevPortal = env => env === undefined;

  createApplication = isDevPortal => async () => {
    const { domain, isPstn } = this.state;
    const { client, onSave } = this.props;
    const currentTeam = TeamContext.getSelectedTeamId();

    if (isNil(domain) || isEmpty(domain)) return;

    const qualifiedDomain = isDevPortal ? `${domain}${utils.validations.defaultDomain}` : domain;

    const applicationName = head(split('.', qualifiedDomain));

    try {
      const { data: { application } } = await client.mutate({
        mutation: UPSERT_APPLICATION,
        variables: {
          application: {
            domain: qualifiedDomain,
            applicationName,
            federationType: isPstn ? 'pstn' : 'none',
          },
          currentTeam,
        },
      });
      onSave(application);
      this.setState({ domain: '' });
    } catch (error) {
      this.setState({
        error: new Error(`Invalid or duplicate application ${domain}`),
      });
    }
  }

  extract = (str, pattern) => (str.match(pattern) || []).pop() || '';

  extractAlphaAndSpecial = str => this.extract(str, '[-.a-zA-Z0-9]+');

  extractAll = str => this.extract(str, '[-a-zA-Z0-9]+');

  flipPstn = () => this.setState(prevState => ({ isPstn: !prevState.isPstn }));

  render() {
    const {
      domain,
      error,
      isPstn,
    } = this.state;

    const {
      classes,
      onClose,
      open,
      env,
    } = this.props;

    const handleChange = this.handleChange(this.isDevPortal(env));

    const createApplication = this.createApplication(this.isDevPortal(env));

    return (
      <Dialog
        open={open}
        onClose={() => onClose() || this.setState(this.baseState)}
        aria-labelledby="create-application-dialog-title"
      >
        <DialogTitle id="create-application-dialog-title">New Application</DialogTitle>
        <FormControl
          error={!isNil(error)}
          classes={{ root: classes.root }}
        >
          <TextField
            label="Domain"
            value={domain || ''}
            onChange={compose(
              this.checkForCollisions,
              handleChange('domain'),
            )}
            type="text"
            inputProps={this.isDevPortal(env) ? { maxLength: 10 } : { maxLength: 30 }}
            placeholder={this.isDevPortal(env) ? 'Application name' : 'name.domain.ext'}
            InputLabelProps={{ shrink: true }}
            InputProps={this.isDevPortal(env) ? { endAdornment: <InputAdornment position="end">.555platform.com</InputAdornment> } : null }
            margin="normal"
          />
          {!this.isDevPortal(env) && <FormControlLabel
            control={(
              <Switch
                checked={isPstn}
                onChange={this.flipPstn}
                value="pstn"
                color="primary"
              />
            )}
            label={`Federation Type: ${isPstn ? 'pstn' : 'none'}`}
          />}
          <Button
            variant="contained"
            classes={{ root: classes.saveButton }}
            disabled={isEmpty(domain) || !isNil(error)}
            onClick={createApplication}
          >
              Save
          </Button>
          {error && domain && (
            <FormHelperText classes={{ root: classes.helperText }}>
              {error.message}
            </FormHelperText>
          )}
        </FormControl>
      </Dialog>
    );
  }
}

UnstyledCreateApplicationDialogue.propTypes = {
  classes: utils.stylesToPropType(styles()).isRequired,
  client: PropTypes.object.isRequired,
  applications: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  env: PropTypes.string,
};

UnstyledCreateApplicationDialogue.defaultProps = {
  applications: [],
  env: undefined,
};

const CreateApplicationDialogue = withStyles(styles)(UnstyledCreateApplicationDialogue);

export default withApollo(CreateApplicationDialogue);
